/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Popup, Button, Base, Text, BookmarkIcon, DownloadIcon, ExclamationTriangleIcon } from "@/shared/ui";
import { useEffect } from "react";
import { useLayout } from "@layout/app/LayoutContext";
import { useTranslation } from "react-i18next";

const typesConstants = {
  alert: { icon: ExclamationTriangleIcon, rightButtonKey: "appPopup.buttons.ok" },
  save: { icon: DownloadIcon, rightButtonKey: "appPopup.buttons.save" },
  question: { icon: BookmarkIcon, rightButtonKey: "appPopup.buttons.confirm" },
};

export type AppPopupProps = {
  visible: boolean;
  type: "alert" | "save" | "question";
  title?: string;
  label?: string;
  onRight: () => void;
  onDiscard?: () => void;
};
const AppPopup: Component = () => {
  const { visible, type, title, label, onRight, onDiscard } = useLayout(
    (state) => state.appPopupProps
  );
  const { updatePopupProps } = useLayout((state) => state);
  const { t } = useTranslation('features/popups');

  const closePopup = () => {
    updatePopupProps({
      visible: false,
      type: "alert",
      title: undefined,
      label: undefined,
/**
 * onRight function.
 *
 * @returns {*} Return value description
 */
      onRight: () => {},
      onDiscard: undefined,
    });
  };

  // Ensure popup closes if it's visible but has no title (invalid state)
  useEffect(() => {
    if (visible && !title) {
      updatePopupProps({
        visible: false,
        type: "alert",
        title: undefined,
        label: undefined,
        onRight: () => {},
        onDiscard: undefined,
      });
    }
  }, [visible, title, updatePopupProps]);

  // Don't render if not visible or if there's no meaningful content
  if (!visible || !title) return <></>;

  return (
    <Popup
      onClose={() => closePopup()}
      icon={typesConstants[type].icon}
      title={title ?? ""}
    >
      <Text c="dimmed">{label}</Text>

      <Base
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          gap: "var(--space-2)",
          paddingTop: "var(--space-2)",
        }}
      >
        {type !== "alert" && (
          <Button variant="outlined" onClick={() => closePopup()}>
            {t('appPopup.buttons.cancel')}
          </Button>
        )}
        {type === "save" && onDiscard && (
          <Button variant="outlined" onClick={onDiscard}>
            {t('appPopup.buttons.discard')}
          </Button>
        )}
        <Button
          onClick={() => {
            onRight();
            closePopup();
          }}
        >
          {t(typesConstants[type].rightButtonKey)}
        </Button>
      </Base>
    </Popup>
  );
};

export default AppPopup;
